// اجزای مشترک AP-ORG-02: کلیدهای Query، فهرست‌های شمارشی، COMP-REPORT-ISSUE (دکمه و دیالوگ گزارش خرابی — US-ORG-052) و COMP-DEVICE-USAGE
// (جزء ثبت دستگاه و شمارنده در فرم جلسه — US-ORG-041؛ مصرف AP-CLN-02).
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CounterUnit,
  type DeviceStatus,
  type DeviceUsageCheck,
  type LiveResourceStatus,
  type ResourceBlockReason,
  type ResourceKind,
  type WorkOrderSeverity,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, FormDialog, latinDigits, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const COUNTER_UNITS: readonly CounterUnit[] = ['Shot', 'Pulse', 'Minute', 'Session', 'None'];
export const DEVICE_STATUSES: readonly DeviceStatus[] = ['Active', 'UnderMaintenance', 'OutOfService', 'Retired'];
export const BLOCK_REASONS: readonly ResourceBlockReason[] = ['Maintenance', 'Cleaning', 'Event', 'Other'];
export const SEVERITIES: readonly WorkOrderSeverity[] = ['Low', 'Medium', 'High', 'Critical'];
export const DEVICES_FEATURE = 'feature.org.devices';

export const deviceKeys = {
  units: (tenantId: string | undefined) => ['organization', 'bookable-units', tenantId] as const,
  blocks: (tenantId: string | undefined) => ['organization', 'resource-blocks', tenantId] as const,
  status: (tenantId: string | undefined, branchId: string | undefined) =>
    ['organization', 'resource-status', tenantId, branchId] as const,
  types: (tenantId: string | undefined) => ['organization', 'device-types', tenantId] as const,
  devices: (tenantId: string | undefined) => ['organization', 'devices', tenantId] as const,
  device: (tenantId: string | undefined, id: string) => ['organization', 'devices', tenantId, id] as const,
  workOrders: (tenantId: string | undefined) => ['organization', 'work-orders', tenantId] as const,
};

/** رنگ وضعیت دستگاه. */
export function deviceTone(status: DeviceStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'UnderMaintenance':
      return 'pending';
    case 'OutOfService':
      return 'error';
    default:
      return 'inactive';
  }
}

/** رنگ وضعیت لحظه‌ای منبع (AC-ORG-022.1). */
export function liveTone(status: LiveResourceStatus): StatusTone {
  switch (status) {
    case 'Free':
      return 'active';
    case 'Occupied':
      return 'in_service';
    case 'Blocked':
      return 'cancelled';
    case 'UnderMaintenance':
      return 'error';
    default:
      return 'inactive';
  }
}

/** درصد از واحد پایه (۱٪ = ۱۰۰) با ارقام فارسی. */
export function percentOf(basisPoints: number | string | null | undefined): string {
  const value = Number(basisPoints ?? 0);
  return faDigits((value / 100).toFixed(1).replace(/\.0$/, ''));
}

/** انواع دستگاه مستأجر. */
export function useDeviceTypes() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: deviceKeys.types(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/device-types')),
  });
}

export interface ReportIssueButtonProps {
  /** نوع منبع. */
  resourceKind: ResourceKind;
  /** منبع. */
  resourceId: string;
  /** نام منبع در عنوان دیالوگ. */
  resourceName: string;
  /** پس از ثبت (تعداد نوبت‌های منتقل‌شده). */
  onReported?: (affectedBookings: number) => void;
  /** شکل دکمه. */
  variant?: 'text' | 'outlined' | 'contained';
}

/**
 * COMP-REPORT-ISSUE (US-ORG-052؛ FLOW-17): دکمه «گزارش خرابی» برای همه کارکنان (<c>org.work_order.report</c> — BR-ORG-053)؛ شدت، شرح و برای
 * بحرانی «توقف استفاده» با وضعیت دستگاه (AC-ORG-052.2) و پیام «n نوبت به صف جابه‌جایی رفت».
 */
export function ReportIssueButton({ resourceKind, resourceId, resourceName, onReported, variant = 'outlined' }: ReportIssueButtonProps) {
  const { t } = useTranslation('org');
  const access = useAccess();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  if (!access.can('org.work_order.report') || !access.featureEnabled(DEVICES_FEATURE)) {
    return null;
  }
  return (
    <>
      <Button
        variant={variant}
        color="warning"
        startIcon={<ReportProblemOutlinedIcon />}
        onClick={() => {
          setDone(null);
          setOpen(true);
        }}
        data-testid="report-issue"
      >
        {t('org:report_issue.button')}
      </Button>
      {done !== null && (
        <Alert severity="success" data-testid="report-issue-done" onClose={() => setDone(null)}>
          {done > 0 ? t('org:report_issue.moved', { n: faDigits(done) }) : t('org:report_issue.done')}
        </Alert>
      )}
      {open && (
        <ReportIssueDialog
          resourceKind={resourceKind}
          resourceId={resourceId}
          resourceName={resourceName}
          onClose={() => setOpen(false)}
          onReported={(affected) => {
            setOpen(false);
            setDone(affected);
            onReported?.(affected);
          }}
        />
      )}
    </>
  );
}

function ReportIssueDialog({
  resourceKind,
  resourceId,
  resourceName,
  onClose,
  onReported,
}: Omit<ReportIssueButtonProps, 'variant' | 'onReported'> & { onClose: () => void; onReported: (affected: number) => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [severity, setSeverity] = useState<WorkOrderSeverity>('Medium');
  const [description, setDescription] = useState('');
  const [stopUse, setStopUse] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState<'UnderMaintenance' | 'OutOfService'>('OutOfService');
  const [touched, setTouched] = useState(false);
  const critical = severity === 'Critical';

  const report = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/organization/work-orders', {
          body: {
            resourceKind,
            resourceId,
            severity,
            description: description.trim(),
            stopUse: critical && stopUse,
            deviceStatus: critical && stopUse && resourceKind === 'Device' ? deviceStatus : null,
            attachmentFileId: null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.workOrders(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
      onReported(Number(result.affectedBookings));
    },
  });

  const invalid = description.trim().length < 3;
  return (
    <FormDialog
      open
      screenId="COMP-REPORT-ISSUE"
      titleKey="org:report_issue.title"
      onSubmit={() => {
        setTouched(true);
        if (!invalid) {
          report.mutate();
        }
      }}
      onClose={onClose}
      isDirty={description.length > 0}
      isSubmitting={report.isPending}
      submitError={report.error ?? undefined}
      submitKey="org:report_issue.submit"
    >
      <Typography variant="body2" color="text.secondary">
        {t('org:report_issue.resource', { name: resourceName })}
      </Typography>
      <TextField
        select
        fullWidth
        label={t('org:report_issue.severity')}
        value={severity}
        onChange={(event) => setSeverity(event.target.value as WorkOrderSeverity)}
        slotProps={{ htmlInput: { 'data-testid': 'report-issue-severity' } }}
      >
        {SEVERITIES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`org:severity.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        multiline
        minRows={3}
        required
        label={t('org:report_issue.description')}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        error={touched && invalid}
        helperText={touched && invalid ? t('common:validation.required') : undefined}
        slotProps={{ htmlInput: { 'data-testid': 'report-issue-description', maxLength: 2000 } }}
      />
      {critical && (
        <Stack spacing={1}>
          <FormControlLabel
            control={<Checkbox checked={stopUse} onChange={(event) => setStopUse(event.target.checked)} data-testid="report-issue-stop" />}
            label={t('org:report_issue.stop_use')}
          />
          {stopUse && resourceKind === 'Device' && (
            <TextField
              select
              fullWidth
              label={t('org:report_issue.device_status')}
              value={deviceStatus}
              onChange={(event) => setDeviceStatus(event.target.value as 'UnderMaintenance' | 'OutOfService')}
              slotProps={{ htmlInput: { 'data-testid': 'report-issue-device-status' } }}
            >
              <MenuItem value="OutOfService">{t('org:device_status.OutOfService')}</MenuItem>
              <MenuItem value="UnderMaintenance">{t('org:device_status.UnderMaintenance')}</MenuItem>
            </TextField>
          )}
          {stopUse && <Alert severity="warning">{t('org:report_issue.stop_warning')}</Alert>}
        </Stack>
      )}
    </FormDialog>
  );
}

export interface DeviceUsageValue {
  deviceId: string;
  quantity: number;
  counterStart: number | null;
  counterEnd: number | null;
  /** یادداشت مغایرت (الزامی وقتی از ۵٪ بیشتر است). */
  note: string;
}

export interface DeviceUsageFieldProps {
  /** شعبه جلسه. */
  branchId: string;
  /** مقدار مورد انتظار (نواحی × پارامتر) از فرم جلسه CLN. */
  expectedQuantity?: number;
  value: DeviceUsageValue | null;
  onChange: (value: DeviceUsageValue | null, check: DeviceUsageCheck | null) => void;
}

function toNumber(text: string): number | null {
  const normalized = latinDigits(text).replace(/[^\d]/g, '');
  return normalized ? Number(normalized) : null;
}

/**
 * COMP-DEVICE-USAGE (US-ORG-041؛ FLOW-17): انتخاب دستگاه Active شعبه (OutOfService نمایش داده نمی‌شود — AC-ORG-041.3)، قرائت ابتدا و انتها با
 * آخرین قرائت ثبت‌شده، مصرف = انتها − ابتدا (AC-ORG-041.2) و هشدار مغایرت بیش از ۵٪ با مقدار مورد انتظار (BR-ORG-041) از
 * <c>POST …/devices/{id}/usage:check</c>. جاسازی در فرم جلسه AP-CLN-02.
 */
export function DeviceUsageField({ branchId, expectedQuantity, value, onChange }: DeviceUsageFieldProps) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const devices = useQuery({
    queryKey: [...deviceKeys.devices(session?.tenantId), 'selectable', branchId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices/selectable', { params: { query: { branchId } } })),
  });
  const device = devices.data?.items.find((item) => item.id === value?.deviceId);
  const [check, setCheck] = useState<DeviceUsageCheck | null>(null);
  const deviceId = value?.deviceId;
  const counterStart = value?.counterStart ?? null;
  const counterEnd = value?.counterEnd ?? null;
  const quantity = expectedQuantity ?? value?.quantity ?? 0;

  useEffect(() => {
    if (!deviceId) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    void unwrap(
      tenantApi.POST('/api/v1/organization/devices/{id}/usage:check', {
        params: { path: { id: deviceId } },
        body: { quantity, counterStart, counterEnd },
      }),
    )
      .then((result) => {
        if (!cancelled) {
          setCheck(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCheck(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantApi, deviceId, quantity, counterStart, counterEnd]);

  const update = (patch: Partial<DeviceUsageValue>) => {
    const next: DeviceUsageValue = {
      deviceId: value?.deviceId ?? '',
      quantity: value?.quantity ?? 0,
      counterStart: value?.counterStart ?? null,
      counterEnd: value?.counterEnd ?? null,
      note: value?.note ?? '',
      ...patch,
    };
    onChange(next.deviceId ? next : null, check);
  };

  return (
    <Stack spacing={1.5} data-testid="device-usage">
      <TextField
        select
        fullWidth
        label={t('org:device_usage.device')}
        value={value?.deviceId ?? ''}
        onChange={(event) => update({ deviceId: event.target.value, counterStart: null, counterEnd: null })}
        slotProps={{ htmlInput: { 'data-testid': 'device-usage-device' } }}
      >
        <MenuItem value="">{t('org:device_usage.none')}</MenuItem>
        {(devices.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      {device && device.lastReading !== null && device.lastReading !== undefined && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('org:device_usage.start')}
            value={value?.counterStart === null || value?.counterStart === undefined ? '' : faDigits(value.counterStart)}
            onChange={(event) => update({ counterStart: toNumber(event.target.value) })}
            helperText={t('org:device_usage.last', { value: faDigits(Number(check?.lastReading ?? device.lastReading)) })}
            slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'device-usage-start' } }}
          />
          <TextField
            fullWidth
            label={t('org:device_usage.end')}
            value={value?.counterEnd === null || value?.counterEnd === undefined ? '' : faDigits(value.counterEnd)}
            onChange={(event) => update({ counterEnd: toNumber(event.target.value) })}
            slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'device-usage-end' } }}
          />
        </Stack>
      )}
      {check && !check.isValid && (
        <Alert severity="error" data-testid="device-usage-invalid">
          {t(`org:device_usage.problem.${check.problem ?? 'device_unavailable'}`, { last: faDigits(Number(check.lastReading ?? 0)) })}
        </Alert>
      )}
      {check?.isValid && check.measuredQuantity !== null && check.measuredQuantity !== undefined && (
        <Typography variant="body2" data-testid="device-usage-measured">
          {t('org:device_usage.measured', {
            n: faDigits(Number(check.measuredQuantity)),
            unit: device ? t(`org:counter_unit.${device.counterUnit}`) : '',
            expected: faDigits(quantity),
          })}
        </Typography>
      )}
      {check?.isValid && check.exceedsThreshold && (
        <Stack spacing={1}>
          <Alert severity="warning" data-testid="device-usage-discrepancy">
            {t('org:device_usage.discrepancy', { percent: percentOf(check.discrepancyBasisPoints) })}
          </Alert>
          <TextField
            fullWidth
            required
            label={t('org:device_usage.note')}
            value={value?.note ?? ''}
            onChange={(event) => update({ note: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'device-usage-note', maxLength: 500 } }}
          />
        </Stack>
      )}
    </Stack>
  );
}

/**
 * لحظه UTC ساعت محلی یک منطقه زمانی (ADR-014: ورود محلی شعبه، ذخیره UTC). تاریخ `yyyy-MM-dd` و ساعت `HH:mm`.
 */
export function zonedToUtcIso(date: string, time: string, timeZone: string): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const offsetAt = (instant: number) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(instant));
    const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
    return Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute')) - instant;
  };
  const first = guess - offsetAt(guess);
  return new Date(guess - offsetAt(first)).toISOString();
}

/** تاریخ کسب‌وکاری امروز در منطقه زمانی (`yyyy-MM-dd`). */
export function todayIn(timeZone: string, addDays = 0): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const [year = 1970, month = 1, day = 1] = parts.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + addDays)).toISOString().slice(0, 10);
}
